import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import StoreButtons from "../common/StoreButtons";
import "./header.css";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const isHome = location.pathname === "/";

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <>
      <header className={`header ${scrolled ? "scrolled glass" : ""}`}>
        <div className="container header-container">
          <div className="logo">
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'inherit' }} onClick={closeMenu}>
              <img src="/images/logo.png" alt="Relax Logo" className="logo-img" />
            </Link>
          </div>
          
          <nav className="nav-links">
            <Link to="/about">About</Link>
            <a href={isHome ? "#features" : "/#features"}>Features</a>
            <a href={isHome ? "#how-it-works" : "/#how-it-works"}>How It Works</a>
            <a href={isHome ? "#testimonials" : "/#testimonials"}>Reviews</a>
          </nav>
          
          <div className="header-actions">
            <div className="header-btn">
              <StoreButtons />
            </div>
            
            <button 
              className="mobile-menu-toggle" 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isMenuOpen ? "close" : "open"}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
                </motion.div>
              </AnimatePresence>
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            className="mobile-nav-overlay"
            initial={{ opacity: 0, y: "-100%" }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            <div className="mobile-nav-content glass">
              <nav className="mobile-nav-links">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                  <Link to="/about" onClick={closeMenu}>About</Link>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                  <a href={isHome ? "#features" : "/#features"} onClick={closeMenu}>Features</a>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                  <a href={isHome ? "#how-it-works" : "/#how-it-works"} onClick={closeMenu}>How It Works</a>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                  <a href={isHome ? "#testimonials" : "/#testimonials"} onClick={closeMenu}>Reviews</a>
                </motion.div>
              </nav>
              <motion.div 
                className="mobile-nav-btn"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
              >
                <StoreButtons />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
